/**
 * Auth bootstrap.
 *
 *  - Mini App mode: exchange initData for a JWT pair.
 *  - Standalone web mode: confirm browser sign-in in the Telegram bot.
 *  - Both modes: persist the refresh token in localStorage so reloads stay signed in.
 */

import { getTelegram } from "@/boot/telegram";
import type { TelegramLoginWidgetPayload } from "@/types/global";

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  expires_in: number;
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let accessExpiresAt = 0;
let sessionRevision = 0;
let refreshInFlight: Promise<boolean> | null = null;
let accessInFlight: Promise<string | null> | null = null;

const REFRESH_STORE_KEY = "prosto.refresh_token";

function readPersistedRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_STORE_KEY);
  } catch {
    return null;
  }
}

function persistRefresh(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(REFRESH_STORE_KEY);
    else localStorage.setItem(REFRESH_STORE_KEY, token);
  } catch {
    /* ignore quota / disabled */
  }
}

function setTokens(pair: TokenPair): void {
  sessionRevision++;
  accessToken = pair.access_token;
  refreshToken = pair.refresh_token;
  accessExpiresAt = Date.now() + pair.expires_in * 1000 - 5_000; // 5 s headroom
  persistRefresh(pair.refresh_token);
}

export function clearTokens(): void {
  sessionRevision++;
  resetTokens();
}

function resetTokens(): void {
  accessToken = null;
  refreshToken = null;
  accessExpiresAt = 0;
  persistRefresh(null);
}

export async function exchangeInitData(): Promise<boolean> {
  const tg = getTelegram();
  if (!tg || !tg.initData) return false;
  const revision = sessionRevision;
  try {
    const response = await fetch("/auth/telegram-initdata", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ init_data: tg.initData }),
    });
    if (!response.ok) return false;
    const pair = (await response.json()) as TokenPair;
    if (sessionRevision !== revision) return false;
    setTokens(pair);
    return true;
  } catch {
    return false;
  }
}

export interface BrowserLogin {
  request_id: string;
  browser_key: string;
  bot_url: string;
  pair_code: string;
  expires_in: number;
}

export async function startBrowserLogin(): Promise<BrowserLogin> {
  const response = await fetch("/auth/browser/start", {
    method: "POST", headers: { "X-Prosto-Login": "1" },
  });
  if (!response.ok) throw new Error("Не удалось начать вход. Попробуй ещё раз.");
  return response.json() as Promise<BrowserLogin>;
}

export async function finishBrowserLogin(login: BrowserLogin): Promise<
  "authenticated" | "pending" | "denied" | "expired"
> {
  const response = await fetch(`/auth/browser/${login.request_id}/complete`, {
    method: "POST",
    headers: { "X-Prosto-Login": "1", "X-Browser-Key": login.browser_key },
  });
  if (response.status === 410) return "expired";
  if (response.status === 403) throw new Error(
    "Вход недоступен. Начни заново в этом браузере и подтверди приглашённым аккаунтом Telegram.",
  );
  if (!response.ok) throw new Error("Не удалось проверить вход. Попробуй ещё раз.");
  const result = await response.json();
  if (result.status === "authenticated") {
    setTokens(result as TokenPair);
    return "authenticated";
  }
  if (result.status === "pending" || result.status === "denied") return result.status;
  throw new Error("Не удалось проверить вход. Начни заново.");
}

export async function exchangeLoginWidget(
  payload: TelegramLoginWidgetPayload,
): Promise<boolean> {
  try {
    const response = await fetch("/auth/telegram-login-widget", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) return false;
    const pair = (await response.json()) as TokenPair;
    setTokens(pair);
    return true;
  } catch {
    return false;
  }
}

export async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const tok = refreshToken ?? readPersistedRefresh();
  if (!tok) return false;
  const pending = refreshSession(tok);
  refreshInFlight = pending;
  try {
    return await pending;
  } finally {
    refreshInFlight = null;
  }
}

async function refreshSession(tok: string): Promise<boolean> {
  const revision = sessionRevision;
  try {
    const response = await fetch("/auth/refresh", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: tok }),
    });
    if (sessionRevision !== revision) return false;
    if (!response.ok) {
      // A tunnel/server outage does not invalidate a user's credentials.
      if (response.status === 401 || response.status === 403) resetTokens();
      return false;
    }
    const pair = (await response.json()) as TokenPair;
    if (sessionRevision !== revision) return false;
    setTokens(pair);
    return true;
  } catch {
    return false;
  }
}

export async function ensureAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessExpiresAt) {
    return accessToken;
  }
  if (accessInFlight) return accessInFlight;
  const revision = sessionRevision;
  const pending = (async () => {
    if (await tryRefresh()) return accessToken;
    // Preserve the session for a retry after a transient refresh error. Also
    // don't start a new Telegram login after the user signed out mid-request.
    if (sessionRevision !== revision || refreshToken || readPersistedRefresh()) return null;
    if (await exchangeInitData()) return accessToken;
    return null;
  })();
  accessInFlight = pending;
  try {
    return await pending;
  } finally {
    accessInFlight = null;
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function hasPersistedSession(): boolean {
  return readPersistedRefresh() !== null;
}
